import React, { useEffect, useMemo, useState } from 'react';
import './AdminModal.css';
import { adminApi } from './utils/adminApi';
import { VALID_BLOCKCHAINS, PLAN_RULES, PLAN_LABELS } from './constants';

const AdminGrantSubscriptionModal = ({ uid, onClose, onGranted }) => {
  const [planId, setPlanId] = useState('custom');
  const [blockchains, setBlockchains] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const rule = PLAN_RULES[planId];

  useEffect(() => {
    if (planId === 'ultimate') {
      setBlockchains([...VALID_BLOCKCHAINS]);
    } else {
      setBlockchains([]);
    }
  }, [planId]);

  const toggleChain = (chain) => {
    if (planId === 'ultimate') return;
    setBlockchains((prev) => {
      if (prev.includes(chain)) return prev.filter((c) => c !== chain);
      if (rule.exact && prev.length >= rule.exact) return prev; // pro: cap at exact count
      if (rule.max && prev.length >= rule.max) return prev; // custom: cap at max
      return [...prev, chain];
    });
  };

  const validationMessage = useMemo(() => {
    if (rule.exact && blockchains.length !== rule.exact) {
      return `${PLAN_LABELS[planId]} requires exactly ${rule.exact} blockchains (${blockchains.length} selected)`;
    }
    if (rule.min && (blockchains.length < rule.min || blockchains.length > rule.max)) {
      return `${PLAN_LABELS[planId]} requires ${rule.min}-${rule.max} blockchains (${blockchains.length} selected)`;
    }
    return '';
  }, [planId, blockchains, rule]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validationMessage) return;
    setSubmitting(true);
    setError('');
    try {
      await adminApi.grantSubscription(uid, { planId, blockchains });
      onGranted();
    } catch (err) {
      console.error('Error granting subscription:', err);
      setError(err.message || 'Failed to grant subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h2>Grant Subscription</h2>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            <div className="admin-field-group">
              <label>Plan</label>
              <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
                <option value="custom">Custom (1-7 blockchains, 3 months)</option>
                <option value="pro">Pro (exactly 3 blockchains, lifetime)</option>
                <option value="ultimate">Ultimate (all 7 blockchains, lifetime)</option>
              </select>
            </div>

            <div className="admin-field-group">
              <label>Blockchains {rule.exact ? `(exactly ${rule.exact})` : `(${rule.min}-${rule.max})`}</label>
              <div className="admin-checkbox-grid">
                {VALID_BLOCKCHAINS.map((chain) => (
                  <label
                    key={chain}
                    className={`admin-checkbox-item ${planId === 'ultimate' ? 'disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={blockchains.includes(chain)}
                      disabled={planId === 'ultimate'}
                      onChange={() => toggleChain(chain)}
                    />
                    {chain}
                  </label>
                ))}
              </div>
              {validationMessage && <div className="admin-field-error">{validationMessage}</div>}
            </div>

            {error && <div className="admin-field-error">{error}</div>}
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={submitting || Boolean(validationMessage)}>
              {submitting ? 'Granting…' : 'Grant Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminGrantSubscriptionModal;
